/* Generated from Java with JSweet 2.3.5 - http://www.jsweet.org */
import { Aai20Document } from './asyncapi/v2/models/Aai20Document';
import { JsonCompat } from './compat/JsonCompat';
import { LoggerCompat } from './compat/LoggerCompat';
import { Constants } from './core/Constants';
import { DocumentFactory } from './core/factories/DocumentFactory';
import { VisitorFactory } from './core/factories/VisitorFactory';
import { DataModelReader } from './core/io/DataModelReader';
import { DataModelReaderDispatcher } from './core/io/DataModelReaderDispatcher';
import { DataModelWriter } from './core/io/DataModelWriter';
import { Document } from './core/models/Document';
import { DocumentType } from './core/models/DocumentType';
import { Node } from './core/models/Node';
import { NodePath } from './core/models/NodePath';
import { ValidationProblem } from './core/models/ValidationProblem';
import { IReferenceResolver } from './core/util/IReferenceResolver';
import { NodePathUtil } from './core/util/NodePathUtil';
import { ReferenceResolverChain } from './core/util/ReferenceResolverChain';
import { VisitorUtil } from './core/util/VisitorUtil';
import { DefaultSeverityRegistry } from './core/validation/DefaultSeverityRegistry';
import { IDocumentValidatorExtension } from './core/validation/IDocumentValidatorExtension';
import { IValidationSeverityRegistry } from './core/validation/IValidationSeverityRegistry';
import { ValidationProblemsResetVisitor } from './core/validation/ValidationProblemsResetVisitor';
import { ValidationVisitor } from './core/validation/ValidationVisitor';
import { IVisitor } from './core/visitors/IVisitor';
import { TraverserDirection } from './core/visitors/TraverserDirection';
import { Oas20Document } from './openapi/v2/models/Oas20Document';
import { Oas30Document } from './openapi/v3/models/Oas30Document';
import { Oas30Operation } from './openapi/v3/models/Oas30Operation';
import { Dereferencer } from './openapi/visitors/dereference/Dereferencer';
import { Oas20to30TransformationVisitor } from './openapi/visitors/transform/Oas20to30TransformationVisitor';

/**
 * The most common entry points into using the data models library.  Provides convenience methods
 * for performing common actions such as i/o, visiting, and validation.
 * @author eric.wittmann@gmail.com
 * @author Jakub Senko <jsenko@redhat.com>
 * @class
 */
export class Library {
    /**
     * Adds a reference resolver to the library.  The resolver will be used whenever the library
     * needs to resolve a $ref reference.
     * @param {*} resolver
     */
    public static addReferenceResolver(resolver : IReferenceResolver) {
        ReferenceResolverChain.getInstance().addResolver(resolver);
    }

    public static removeReferenceResolver(resolver : IReferenceResolver) {
        ReferenceResolverChain.getInstance().removeResolver(resolver);
    }

    /**
     * Creates a new, empty document of the given type.
     * @param {DocumentType} type
     * @return {Document}
     */
    public static createDocument(type : DocumentType) : Document {
        switch((type)) {
        case DocumentType.asyncapi2:
            return new Aai20Document();
        case DocumentType.openapi2:
            return new Oas20Document();
        case DocumentType.openapi3:
            return new Oas30Document();
        default:
            throw Object.defineProperty(new Error("Unknown document type: " + type), '__classes', { configurable: true, value: ['java.lang.Throwable','java.lang.Object','java.lang.RuntimeException','java.lang.Exception'] });
        }
    }

    /**
     * Called to create a node path for a given data model node.
     * @param {Node} node
     * @return {NodePath}
     */
    public static createNodePath(node : Node) : NodePath {
        return NodePathUtil.createNodePath(node);
    }

    /**
     * Convenience method for visiting a single data model nodel.
     * @param {Node} node
     * @param {*} visitor
     */
    public static visitNode(node : Node, visitor : IVisitor) {
        VisitorUtil.visitNode(node, visitor);
    }

    /**
     * Convenience method for visiting a data model tree.  Supports traversing the data model either
     * up or down the tree.
     * @param {Node} node
     * @param {*} visitor
     * @param {TraverserDirection} direction
     */
    public static visitTree(node : Node, visitor : IVisitor, direction : TraverserDirection) {
        VisitorUtil.visitTree(node, visitor, direction);
    }

    /**
     * @deprecated
     * This method has been deprecated. It will continue to be supported but will be removed in a future release.
     * <p> Use {@link Library#validateDocument(Node, IValidationSeverityRegistry, List)} instead.
     * 
     * Called to validate a data model node.  All validation rules will be evaluated and reported.  The list
     * of validation problems found during validation is returned.  In addition, validation problems will be
     * reported on the individual nodes themselves.  Validation problem severity is determined by checking
     * with the included severity registry.  If the severity registry is null, a default registry is used.
     * @param {Node} node
     * @param {*} severityRegistry
     * @return {ValidationProblem[]}
     */
    public static validate(node : Node, severityRegistry : IValidationSeverityRegistry) : Array<ValidationProblem> {
        if(severityRegistry == null) {
            severityRegistry = new DefaultSeverityRegistry();
        }
        let resetter : ValidationProblemsResetVisitor = VisitorFactory.createValidationProblemsResetVisitor(node.ownerDocument());
        Library.visitTree(node, resetter, TraverserDirection.down);
        let validator : ValidationVisitor = VisitorFactory.createValidationVisitor(node.ownerDocument());
        validator.setSeverityRegistry(severityRegistry);
        Library.visitTree(node, validator, TraverserDirection.down);
        return validator.getValidationProblems();
    }

    /**
     * Called to validate a data model node.  All validation rules will be evaluated and reported.  The list
     * of validation problems found during validation is returned.  In addition, validation problems will be
     * reported on the individual nodes themselves.  Validation problem severity is determined by checking
     * with the included severity registry.  If the severity registry is null, a default registry is used.
     * Custom validators can be passed to provide additional validation rules beyond what this Library offers out of the box.
     * 
     * @param {Node} node The document to be validated
     * @param {*} severityRegistry Supply a custom severity registry. If nothing is passed, the default severity registry will be used
     * @param {*[]} extensions Supply an optional list of validation extensions, enabling the use of 3rd-party validators or custom validation rules
     * @return {Promise} full list of the validation problems found in the document
     */
    public static  async validateDocument(node : Node, severityRegistry : IValidationSeverityRegistry, extensions : Array<IDocumentValidatorExtension>) :  Promise< Promise<Array<ValidationProblem>> >  {
        let problems : Array<ValidationProblem> = Library.validate(node, severityRegistry);
        node.addValidationProblems(problems);
        if(extensions == null || /* isEmpty */(extensions.length == 0)) {
            return /* completedFuture */Promise.resolve(problems);
        }
        for(let index236=0; index236 < extensions.length; index236++) {
            let extension = extensions[index236];
            {
                try {
                    let extProblems : Array<ValidationProblem> = /* get */await extension.validateDocument(node);
                    node.addValidationProblems(extProblems);
                } catch(e) {
                    LoggerCompat.info("Error occurred in validation extension \'%o\': %o", /* getName */(c => c["__class"]?c["__class"]:c["name"])((<any>extension.constructor)), e.message);
                };
            }
        }
        return /* completedFuture */Promise.resolve(node.getValidationProblems());
    }

    /**
     * Reads an entire document from JSON data.  The JSON data (already parsed, not in string format) is
     * read as a data model {@link Document} and returned.
     * @param {*} json
     * @return {Document}
     */
    public static readDocument(json : any) : Document {
        let clonedJson : any = /* clone */JsonCompat.clone(json);
        let type : DocumentType = Library.discoverDocumentType(clonedJson);
        let reader : DataModelReader = VisitorFactory.createDataModelReader(type);
        let document : Document = DocumentFactory.create(type);
        reader.readDocument(clonedJson, document);
        return document;
    }

    /**
     * Reads an entire document from a JSON formatted string.  This will parse the given string into
     * JSON data and then call Library.readDocument.
     * @param {string} jsonString
     * @return {Document}
     */
    public static readDocumentFromJSONString(jsonString : string) : Document {
        let json : any = JsonCompat.parseJSON(jsonString);
        return Library.readDocument(json);
    }

    /**
     * Call this to do a "partial read" on a given node.  You must pass the JSON data for the node
     * type and an empty instance of the property node class.  For example, you could read just an
     * Operation by passing the JSON data for the operation along with an instance of e.g.
     * {@link Oas30Operation} and this will read the data and store it in the instance.
     * @param {*} json
     * @param {Node} node
     * @return {Node}
     */
    public static readNode(json : any, node : Node) : Node {
        let clonedJson : any = /* clone */JsonCompat.clone(json);
        let type : DocumentType = node.ownerDocument().getDocumentType();
        let reader : DataModelReader = VisitorFactory.createDataModelReader(type);
        let dispatcher : DataModelReaderDispatcher = VisitorFactory.createDataModelReaderDispatcher(type, clonedJson, reader);
        Library.visitNode(node, dispatcher);
        return node;
    }

    /**
     * Called to serialize a given data model node to a JSON object.
     * @param {Node} node
     * @return {*}
     */
    public static writeNode(node : Node) : any {
        let writer : DataModelWriter = VisitorFactory.createDataModelWriter(node.ownerDocument());
        Library.visitTree(node, writer, TraverserDirection.down);
        return writer.getResult();
    }

    /**
     * Called to serialize a given data model to a JSON formatted string.
     * @param {Document} document
     * @return {string}
     */
    public static writeDocumentToJSONString(document : Document) : string {
        let json : any = Library.writeNode(document);
        return JsonCompat.stringify(json);
    }

    /**
     * Called to discover what type of document the given JSON data represents.  This method
     * interrogates the following appropriate top level properties:
     * 
     * - asyncapi
     * - openapi
     * - swagger
     * 
     * Based on which property is defined, and the value of that property, the correct document
     * type is determined and returned.
     * 
     * @param {*} json
     * @return {DocumentType}
     */
    public static discoverDocumentType(json : any) : DocumentType {
        let asyncapi : string = JsonCompat.getPropertyString(json, Constants.PROP_ASYNCAPI);
        let openapi : string = JsonCompat.getPropertyString(json, Constants.PROP_OPENAPI);
        let swagger : string = JsonCompat.getPropertyString(json, Constants.PROP_SWAGGER);
        if(asyncapi != null && /* startsWith */((str, searchString, position = 0) => str.substr(position, searchString.length) === searchString)(asyncapi, "2.")) {
            return DocumentType.asyncapi2;
        }
        if(openapi != null) {
            if(/* startsWith */((str, searchString, position = 0) => str.substr(position, searchString.length) === searchString)(openapi, "2.")) {
                return DocumentType.openapi2;
            }
            if(/* startsWith */((str, searchString, position = 0) => str.substr(position, searchString.length) === searchString)(openapi, "3.0")) {
                return DocumentType.openapi3;
            }
        }
        if(swagger != null) {
            if(/* startsWith */((str, searchString, position = 0) => str.substr(position, searchString.length) === searchString)(swagger, "2.")) {
                return DocumentType.openapi2;
            }
        }
        throw Object.defineProperty(new Error("Unknown data model type or version."), '__classes', { configurable: true, value: ['java.lang.Throwable','java.lang.Object','java.lang.RuntimeException','java.lang.Exception'] });
    }

    /**
     * Clones the given document by serializing it to a JS object, and then re-parsing it.
     * @param {Document} source
     * @return {Document}
     */
    public static cloneDocument(source : Document) : Document {
        let jsObj : any = Library.writeNode(source);
        return Library.readDocument(jsObj);
    }

    /**
     * Transforms from an OpenAPI 2.0 document into a 3.0 document.
     * @param {Oas20Document} source
     * @return {Oas30Document}
     */
    public static transformDocument(source : Oas20Document) : Oas30Document {
        let clone : Oas20Document = <Oas20Document>Library.cloneDocument(source);
        let transformer : Oas20to30TransformationVisitor = new Oas20to30TransformationVisitor();
        VisitorUtil.visitTree(clone, transformer, TraverserDirection.down);
        return transformer.getResult();
    }

    public static dereferenceDocument$io_apicurio_datamodels_core_models_Document(source : Document) : Document {
        let rl : Dereferencer = new Dereferencer(source);
        return rl.dereference();
    }

    public static dereferenceDocument$io_apicurio_datamodels_core_models_Document$boolean(source : Document, strict : boolean) : Document {
        let rl : Dereferencer = new Dereferencer(source, ReferenceResolverChain.getInstance(), strict);
        return rl.dereference();
    }

    public static dereferenceDocument$io_apicurio_datamodels_core_models_Document$io_apicurio_datamodels_core_util_IReferenceResolver$boolean(source : Document, resolver : IReferenceResolver, strict : boolean) : Document {
        let rl : Dereferencer = new Dereferencer(source, resolver, strict);
        return rl.dereference();
    }

    /**
     * Dereferences a document - this will take all external references ($ref) found in
     * the document and pull them into this document.  It will then update any external
     * reference to instead point to the local copy.  The result is a functionally
     * equivalent document with all resolvable external references removed.
     * 
     * @param {Document} source the source document
     * @param {*} resolver a custom reference resolver to use on this dereference operation
     * @param {boolean} strict if true, throws an exception if unresolvable references remain
     * @return {Document}
     */
    public static dereferenceDocument(source? : any, resolver? : any, strict? : any) : any {
        if(((source != null && source instanceof <any>Document) || source === null) && ((resolver != null && (resolver["__interfaces"] != null && resolver["__interfaces"].indexOf("io.apicurio.datamodels.core.util.IReferenceResolver") >= 0 || resolver.constructor != null && resolver.constructor["__interfaces"] != null && resolver.constructor["__interfaces"].indexOf("io.apicurio.datamodels.core.util.IReferenceResolver") >= 0)) || resolver === null) && ((typeof strict === 'boolean') || strict === null)) {
            return <any>Library.dereferenceDocument$io_apicurio_datamodels_core_models_Document$io_apicurio_datamodels_core_util_IReferenceResolver$boolean(source, resolver, strict);
        } else if(((source != null && source instanceof <any>Document) || source === null) && ((typeof resolver === 'boolean') || resolver === null) && strict === undefined) {
            return <any>Library.dereferenceDocument$io_apicurio_datamodels_core_models_Document$boolean(source, resolver);
        } else if(((source != null && source instanceof <any>Document) || source === null) && resolver === undefined && strict === undefined) {
            return <any>Library.dereferenceDocument$io_apicurio_datamodels_core_models_Document(source);
        } else throw new Error('invalid overload');
    }
}
Library["__class"] = "io.apicurio.datamodels.Library";



